/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import styled from "styled-components";
import { Dropdown, theme, Text, Button, FlexContainer } from "@nordcloud/gnui";
import { SortOrder } from "~/generated/graphql";
import { MOBILE_MAX_WIDTH } from "~/constants";

export type Option = {
  value: string;
  label: string;
};

type Props<T> = {
  options: Option[];
  value: string;
  direction: SortOrder;
  onSelect: (filter: T) => void;
  onSortChange: (direction: SortOrder) => void;
};

const SORT_DROPDOWN_WIDTH = "15rem";

const SortSelectorRoot = styled(FlexContainer)`
  width: auto;
  min-width: 0;
  flex: 0 0 auto;

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    width: 100%;
  }
`;

const SortSelectorControls = styled(FlexContainer)`
  min-width: 0;

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    flex: 1;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing.spacing02};
  }

  @media (min-width: ${theme.breakpoints.md}px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SortLabel = styled(Text)`
  margin: 0;

  @media (min-width: ${theme.breakpoints.md}px) {
    white-space: nowrap;
    margin-right: ${theme.spacing.spacing04};
  }
`;

const StyledDropdown = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  max-width: 100%;

  @media (min-width: ${theme.breakpoints.md}px) {
    flex: 0 0 ${SORT_DROPDOWN_WIDTH};
    width: ${SORT_DROPDOWN_WIDTH};
    max-width: ${SORT_DROPDOWN_WIDTH};
  }

  & > button {
    position: absolute;
    right: calc(${theme.spacing.spacing01} + 1px);
    top: calc(${theme.spacing.spacing01} + 1px);
  }
`;

export function SortSelector<T>({
  options,
  value,
  direction,
  onSelect,
  onSortChange,
}: Props<T>) {
  const isAscending = direction === SortOrder.Asc;

  return (
    <SortSelectorRoot justifyContent="space-between">
      <SortSelectorControls>
        <SortLabel tag="div">Sort by:</SortLabel>
        <StyledDropdown>
          <Dropdown
            data-testid="sort-dropdown"
            name="Select"
            options={options}
            onChange={onSelect as (filter: T | string) => void}
            value={value}
            width="100%"
            minNumOfOptionsToShowSearchBox={10}
          />
          <Button
            size="md"
            severity="low"
            data-testid="sort-button"
            icon={isAscending ? "sortAscending" : "sortDescending"}
            onClick={() =>
              onSortChange(isAscending ? SortOrder.Desc : SortOrder.Asc)
            }
            type="button"
          />
        </StyledDropdown>
      </SortSelectorControls>
    </SortSelectorRoot>
  );
}
